import api from "../../../../../../api/api";
import { getAIGeneratedLessons } from "./utils";

/**
 * Background poller for AI-generated course build status.
 *
 * Lives at module scope (not inside a component) so the interval survives React
 * unmounts and SPA navigation, and persists its session to localStorage so a full
 * page refresh resumes it. The ONLY thing that stops it is the status API
 * answering { action: "done" }.
 *
 * The API drives the whole flow through its top-level `action`:
 *   "wait"           -> a course is still building; poll again
 *   "create_lessons" -> POST create-lessons for `course_id`, then keep polling
 *   "done"           -> everything is built; stop
 */

const STORAGE_KEY = "ai_course_status_polling";
export const AI_COURSE_POLL_INTERVAL_MS = 60 * 1000;

let intervalId = null; // the single live interval — never more than one
let isFetching = false; // a request is in flight; skip overlapping ticks
let activeSession = null; // { courseIds, teacherByCourseId, lessonsRequestedIds }

const readStoredSession = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.courseIds) || parsed.courseIds.length === 0) {
      return null;
    }

    return {
      courseIds: parsed.courseIds.map(String),
      teacherByCourseId: parsed.teacherByCourseId ?? {},
      // Courses we've already POSTed create-lessons for — the API keeps asking
      // until the build registers, and we must not create the lessons twice.
      lessonsRequestedIds: Array.isArray(parsed?.lessonsRequestedIds)
        ? parsed.lessonsRequestedIds.map(String)
        : [],
    };
  } catch (error) {
    console.error("Failed to read AI course polling session:", error);
    return null;
  }
};

const writeStoredSession = (session) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch (error) {
    // Storage full / disabled — polling still works for this page life.
    console.error("Failed to persist AI course polling session:", error);
  }
};

/** Stops the interval and clears the persisted session so it won't resume. */
export const stopAiCourseStatusPolling = () => {
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
  isFetching = false;
  activeSession = null;

  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error("Failed to clear AI course polling session:", error);
  }
};

export const isAiCourseStatusPollingRunning = () => intervalId !== null;

/**
 * Teacher for the create-lessons payload. The status API returns only a
 * course_id, so we look it up in the snapshot; AI courses for a student all come
 * from the same teacher, so any known teacher is a safe fallback for a course
 * that wasn't in the original list.
 */
const resolveTeacherId = (session, courseKey) =>
  session.teacherByCourseId?.[courseKey] ??
  Object.values(session.teacherByCourseId ?? {}).find(
    (teacher) => teacher !== undefined && teacher !== null,
  );

const fetchAiCourseStatus = async (token) => {
  // Previous tick is still waiting on the API — don't stack requests.
  if (isFetching) return;

  const session = activeSession ?? readStoredSession();
  if (!session) {
    stopAiCourseStatusPolling();
    return;
  }
  activeSession = session;

  isFetching = true;
  try {
    const { data } = await api.get(
      `/courses/get-status-ai-courses/?course_ids=${encodeURIComponent(
        session.courseIds.join(","),
      )}`,
      { headers: { Authorization: `Token ${token}` } },
    );

    const { action, course_id: courseId } = data ?? {};

    // Every course is built — the only exit.
    if (action === "done") {
      stopAiCourseStatusPolling();
      return;
    }

    // "wait" (or anything unrecognised): the build is still running, poll again.
    if (action !== "create_lessons") return;

    if (courseId === null || courseId === undefined) return;

    const courseKey = String(courseId);
    // The API keeps returning create_lessons until the build registers, so a
    // second POST for the same course would duplicate its lessons.
    if (session.lessonsRequestedIds.includes(courseKey)) return;

    session.lessonsRequestedIds.push(courseKey);
    writeStoredSession(session); // persist first — a refresh mid-POST must not re-fire it

    await getAIGeneratedLessons(
      token,
      courseId,
      resolveTeacherId(session, courseKey),
    );
  } catch (error) {
    // Transient failure — keep the interval alive and retry on the next tick.
    console.error("Failed to fetch AI course generation status:", error);
  } finally {
    isFetching = false;
  }
};

/**
 * Starts polling for the given courses, or resumes a persisted session when
 * called with no courseIds (e.g. after a page refresh).
 * A no-op if an interval is already running.
 */
export const startAiCourseStatusPolling = ({
  token,
  courseIds,
  teacherByCourseId = {},
} = {}) => {
  if (!token) return;
  if (intervalId !== null) return; // already polling — don't create a second interval

  if (Array.isArray(courseIds) && courseIds.length > 0) {
    activeSession = {
      courseIds: courseIds.map(String),
      teacherByCourseId,
      lessonsRequestedIds: [],
    };
    writeStoredSession(activeSession);
  } else {
    activeSession = readStoredSession();
    if (!activeSession) return; // nothing pending and nothing to resume
  }

  intervalId = setInterval(
    () => fetchAiCourseStatus(token),
    AI_COURSE_POLL_INTERVAL_MS,
  );
};

/** Resumes a session persisted by a previous page load, if there is one. */
export const resumeAiCourseStatusPolling = (token) =>
  startAiCourseStatusPolling({ token });
